/**
 * HTML da proposta comercial HangSix (PDF via html2canvas).
 * Usado por src/utils/propostaDocumento (Calculadora Solar e Consulta de Propostas).
 * Placeholders de texto já escapados pelo caller.
 */

export const escapeHtmlHangSix = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const fmtEixoReais = (n) => {
  const v = Number(n) || 0
  if (Math.abs(v) >= 1000) {
    const mil = v / 1000
    const txt = mil.toLocaleString('pt-BR', {
      minimumFractionDigits: mil % 1 === 0 ? 0 : 1,
      maximumFractionDigits: 1
    })
    return `${txt} mil`
  }
  return v.toLocaleString('pt-BR', { maximumFractionDigits: 0 })
}

function montarSvgPayback(investimento, economiaAnual) {
  const inv = Math.max(0, Number(investimento) || 0)
  const eco = Math.max(0, Number(economiaAnual) || 0)
  const anos = 8
  const acumulado = Array.from({ length: anos }, (_, i) => eco * (i + 1))
  const maxVal = Math.max(inv, acumulado[anos - 1] || 0, 1)
  const yBase = 168
  const yTop = 28
  const plotH = yBase - yTop
  const x0 = 62
  const x1 = 784
  const slot = (x1 - x0) / anos
  const barW = 36
  const altura = (v) => Math.max(v > 0 ? 3 : 0, (v / maxVal) * plotH)
  const yBar = (v) => yBase - altura(v)
  const paybackAno = eco > 0 ? Math.ceil(inv / eco) : 0

  const grades = [1, 2 / 3, 1 / 3]
    .map((p) => {
      const gy = yTop + plotH * (1 - p)
      return `<line x1="${x0}" y1="${gy}" x2="${x1}" y2="${gy}" stroke="#E0E0E0" stroke-dasharray="3,3" />
        <text x="${x0 - 6}" y="${gy + 3}" font-size="9" text-anchor="end" fill="#888">${fmtEixoReais(maxVal * p)}</text>`
    })
    .join('')

  const yInv = yBar(inv)
  const linhaInv = inv > 0
    ? `<line x1="${x0}" y1="${yInv}" x2="${x1}" y2="${yInv}" stroke="#2C3E50" stroke-width="1.6" stroke-dasharray="6,4" />
      <text x="${x1}" y="${yInv - 5}" font-size="9" text-anchor="end" fill="#2C3E50">Investimento</text>`
    : ''

  const barras = acumulado
    .map((valor, i) => {
      const gx = x0 + slot * i
      const x = gx + slot / 2 - barW / 2
      const recuperou = paybackAno > 0 && i + 1 >= paybackAno
      const fill = recuperou ? '#F69911' : '#FA670A'
      const yTxt = Math.max(18, yBar(valor) - 5)
      return `<rect x="${x}" y="${yBar(valor)}" width="${barW}" height="${altura(valor)}" fill="${fill}" rx="3" />
        <text x="${gx + slot / 2}" y="${yTxt}" font-size="8" text-anchor="middle" fill="#555">${fmtEixoReais(valor)}</text>
        <text x="${gx + slot / 2}" y="${yBase + 14}" font-size="10" text-anchor="middle" fill="#666">Ano ${i + 1}</text>`
    })
    .join('')

  return `<svg width="100%" height="100%" viewBox="0 0 800 190" preserveAspectRatio="xMidYMid meet">
      ${grades}
      <line x1="${x0}" y1="${yBase}" x2="${x1}" y2="${yBase}" stroke="#CBD5E1" stroke-width="1.5" />
      <text x="8" y="14" font-size="9" fill="#888">R$</text>
      ${barras}
      ${linhaInv}
    </svg>`
}

/**
 * @param {object} p
 */
export function buildHangSixPropostaHtml(p) {
  const {
    logoSrc,
    nomeCliente,
    dataProposta,
    dataValidade,
    cpfLinha,
    consumoStr,
    kwpStr,
    inversorStr,
    modulosStr,
    investimentoStr,
    investimentoValor,
    economiaAnualStr,
    economiaAnualValor,
    paybackAnosStr,
    empresaNome,
    empresaSlogan,
    contatoStr,
    enderecoStr,
    gallery,
    autoconsumoSrc
  } = p

  const [g0, g1, g2, g3] = gallery
  const chartSvg = montarSvgPayback(investimentoValor, economiaAnualValor)

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Proposta Comercial - Energia Solar Fotovoltaica</title>
    <style>
        :root {
            --primary: #FA670A;
            --secondary: #F69911;
            --light: #FFFFFF;
            --dark-bg: #121212;
            --card-bg: #FFFFFF;
            --text-color: #333333;
            --text-muted: #666666;
            --gray-bg: #F8F9FA;
            --border-color: #E2E8F0;
        }

        * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
            font-family: 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }

        body {
            background-color: #FFFFFF;
            color: var(--text-color);
            line-height: 1.6;
            padding: 0;
        }

        .proposal-container {
            width: 210mm;
            margin: 0 auto;
            background: #FFFFFF;
        }

        .proposal-page {
            width: 210mm;
            background: #FFFFFF;
        }

        .proposal-page-full {
            width: 210mm;
            height: 297mm;
            min-height: 297mm;
            max-height: 297mm;
            overflow: hidden;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            padding: 10mm 12mm;
        }

        /* HEADER SECTION */
        .header {
            background: linear-gradient(135deg, var(--secondary) 0%, var(--primary) 100%);
            color: #FFFFFF;
            padding: 30px 40px;
            display: table;
            width: 100%;
        }

        .logo-placeholder {
            display: table-cell;
            vertical-align: middle;
            width: 40%;
        }

        .logo-box {
            width: 200px;
            height: 75px;
            border-radius: 8px;
            background: transparent;
            overflow: hidden;
            text-align: center;
        }

        .logo-box img {
            width: 200px;
            height: 75px;
            object-fit: contain;
            display: block;
        }

        .header-title {
            display: table-cell;
            vertical-align: middle;
            text-align: right;
            width: 60%;
        }

        .header-title h1 {
            font-size: 26px;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 5px;
            font-weight: 800;
        }

        .header-title p {
            font-size: 14px;
            opacity: 0.95;
            font-weight: 300;
        }

        /* CONTENT CONTAINER */
        .content {
            padding: 35px 40px;
        }

        .section-title {
            color: var(--primary);
            font-size: 18px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 18px;
            margin-top: 15px;
            padding-bottom: 6px;
            border-bottom: 2px solid var(--secondary);
            display: inline-block;
        }

        /* GRID / CARDS */
        .grid-2 {
            display: table;
            width: 100%;
            margin-bottom: 25px;
        }

        .col-half {
            display: table-cell;
            width: 50%;
            vertical-align: top;
        }

        .col-half:first-child {
            padding-right: 15px;
        }

        .col-half:last-child {
            padding-left: 15px;
        }

        .info-card {
            background-color: var(--gray-bg);
            border-left: 4px solid var(--primary);
            border-radius: 6px;
            padding: 18px 20px;
            height: 100%;
        }

        .info-card h3 {
            color: var(--primary);
            font-size: 15px;
            margin-bottom: 12px;
            text-transform: uppercase;
        }

        .info-list {
            list-style: none;
        }

        .info-list li {
            margin-bottom: 8px;
            font-size: 14px;
            display: table;
            width: 100%;
        }

        .info-list li strong {
            display: table-cell;
            color: #555;
            width: 45%;
        }

        .info-list li span {
            display: table-cell;
            font-weight: 600;
            color: #111;
            width: 55%;
        }

        .about-card {
            background-color: var(--gray-bg);
            border-left: 4px solid var(--primary);
            border-radius: 6px;
            padding: 16px 18px 14px;
            margin-top: 4px;
        }

        .about-card > h3 {
            color: var(--primary);
            font-size: 15px;
            margin-bottom: 10px;
            text-transform: uppercase;
        }

        .about-card > p {
            font-size: 12px;
            line-height: 1.45;
            color: var(--text-color);
            margin-bottom: 8px;
        }

        .about-grid {
            display: table;
            width: 100%;
            margin-top: 8px;
            table-layout: fixed;
        }

        .about-col {
            display: table-cell;
            width: 50%;
            vertical-align: top;
        }

        .about-col:first-child {
            padding-right: 12px;
        }

        .about-col:last-child {
            padding-left: 12px;
            border-left: 1px solid var(--border-color);
        }

        .about-col h4 {
            color: var(--primary);
            font-size: 12.5px;
            font-weight: 700;
            margin: 0 0 6px;
        }

        .about-col p {
            font-size: 11.5px;
            line-height: 1.4;
            color: var(--text-color);
            margin-bottom: 6px;
        }

        .about-list {
            list-style: none;
            margin: 4px 0 8px;
        }

        .about-list li {
            position: relative;
            padding-left: 14px;
            margin-bottom: 3px;
            font-size: 11.5px;
            line-height: 1.35;
            color: var(--text-color);
        }

        .about-list li::before {
            content: "•";
            color: var(--primary);
            font-weight: 700;
            position: absolute;
            left: 2px;
            top: -1px;
        }

        /* COMO FUNCIONA O SISTEMA DE AUTOCONSUMO */
        .autoconsumo-section {
            margin: 25px 0 30px;
        }

        .proposal-page-full .autoconsumo-section {
            margin: 0;
            flex: 1;
            display: flex;
            flex-direction: column;
            min-height: 0;
            height: 100%;
        }

        .ac-banner {
            background-color: #FA670A;
            color: #ffffff;
            padding: 16px 20px;
            border-radius: 10px 10px 0 0;
            border-bottom: 4px solid #F69911;
            text-align: center;
        }

        .ac-banner h2 {
            font-size: 16px;
            font-weight: 700;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            color: #ffffff;
        }

        .ac-banner p {
            font-size: 11px;
            color: #fff3e6;
            margin-top: 4px;
        }

        .diagram-container {
            background-color: #ffffff;
            border: 1px solid #fde8d7;
            border-top: none;
            padding: 15px;
        }

        .proposal-page-full .diagram-container {
            flex: 1;
            display: flex;
            flex-direction: column;
            min-height: 0;
            border-radius: 0 0 10px 10px;
        }

        .diagram-title {
            text-align: center;
            font-weight: 700;
            color: #FA670A;
            font-size: 12px;
            margin-bottom: 10px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .svg-wrapper {
            text-align: center;
            width: 100%;
            overflow: hidden;
            background: #ffffff;
            border-radius: 6px;
        }

        .svg-wrapper img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 0 auto;
        }

        .proposal-page-full .svg-wrapper {
            flex: 1;
            min-height: 0;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .proposal-page-full .svg-wrapper img {
            max-width: 100%;
            max-height: 100%;
            width: auto;
            height: auto;
            object-fit: contain;
        }

        .flow-section {
            background-color: #ffffff;
            border: 1px solid #fde8d7;
            border-radius: 10px;
            padding: 15px;
            margin-bottom: 25px;
        }

        .ac-section-heading {
            font-size: 12px;
            font-weight: 700;
            color: #2d1810;
            border-left: 4px solid #FA670A;
            padding-left: 10px;
            margin-bottom: 12px;
            text-transform: uppercase;
        }

        .flow-grid {
            width: 100%;
            border-collapse: separate;
            border-spacing: 8px;
        }

        .flow-grid td {
            width: 50%;
            vertical-align: top;
            padding: 0;
        }

        .flow-item {
            background-color: #fffaf5;
            border: 1px solid #fde8d7;
            border-radius: 6px;
            padding: 12px;
            height: 100%;
        }

        .badge {
            display: inline-block;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 9px;
            font-weight: bold;
            color: #ffffff;
            margin-bottom: 6px;
            text-transform: uppercase;
        }

        .bg-orange1 { background-color: #F69911; }
        .bg-orange2 { background-color: #FA670A; }
        .bg-dark { background-color: #2d1810; }
        .bg-accent { background-color: #c84b00; }

        .flow-item-title {
            font-weight: 700;
            font-size: 12px;
            margin-bottom: 5px;
            text-transform: uppercase;
        }

        .flow-item-title.gerada { color: #FA670A; }
        .flow-item-title.auto { color: #F69911; }
        .flow-item-title.injetada { color: #c84b00; }
        .flow-item-title.consumida { color: #2d1810; }

        .flow-item p {
            font-size: 10px;
            color: #4a2c1d;
            line-height: 1.4;
        }

        .summary-box {
            background-color: #fff3e6;
            border: 1px solid #fcdcb8;
            border-radius: 6px;
            padding: 12px;
            margin-top: 8px;
            font-size: 10px;
            color: #7a2e00;
            line-height: 1.4;
        }

        .summary-box strong {
            color: #FA670A;
        }

        /* SEÇÃO SISTEMAS INSTALADOS */
        .projects-grid {
            display: table;
            width: 100%;
            margin-bottom: 30px;
            table-layout: fixed;
        }

        .project-row {
            display: table-row;
        }

        .project-card {
            display: table-cell;
            width: 25%;
            padding: 8px;
            vertical-align: top;
        }

        .project-card-inner {
            background-color: #FFFFFF;
            border: 1px solid var(--border-color);
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 3px 10px rgba(0,0,0,0.05);
            height: 100%;
        }

        .project-img-placeholder {
            width: 100%;
            height: 120px;
            background: #EAEAEA;
            border-bottom: 2px solid var(--secondary);
            overflow: hidden;
        }

        .project-img-placeholder img {
            width: 100%;
            height: 120px;
            object-fit: cover;
            display: block;
        }

        .project-details {
            padding: 12px;
        }

        .project-details h4 {
            font-size: 13px;
            color: var(--primary);
            margin-bottom: 6px;
            font-weight: 700;
        }

        .project-details p {
            font-size: 11px;
            color: var(--text-muted);
            margin-bottom: 4px;
            line-height: 1.3;
        }

        .project-tag {
            display: inline-block;
            background-color: rgba(246, 153, 17, 0.15);
            color: var(--primary);
            font-weight: bold;
            font-size: 10px;
            padding: 2px 6px;
            border-radius: 4px;
            margin-top: 5px;
        }

        /* SEÇÃO GRÁFICO CONSUMO X GERAÇÃO */
        .chart-container {
            background-color: var(--gray-bg);
            border: 1px solid var(--border-color);
            border-radius: 10px;
            padding: 18px 22px;
            margin-bottom: 18px;
        }

        .chart-header {
            display: table;
            width: 100%;
            margin-bottom: 15px;
        }

        .chart-title-box {
            display: table-cell;
            vertical-align: middle;
        }

        .chart-title-box h3 {
            color: var(--primary);
            font-size: 16px;
            font-weight: 700;
        }

        .chart-legend {
            display: table-cell;
            text-align: right;
            vertical-align: middle;
            font-size: 12px;
        }

        .legend-item {
            display: inline-block;
            margin-left: 15px;
        }

        .legend-color {
            display: inline-block;
            width: 12px;
            height: 12px;
            border-radius: 3px;
            margin-right: 4px;
            vertical-align: middle;
        }

        .legend-line {
            display: inline-block;
            width: 16px;
            height: 0;
            border-top: 2px dashed #2C3E50;
            margin-right: 4px;
            vertical-align: middle;
        }

        .chart-svg-box {
            width: 100%;
            height: 200px;
            background: #FFFFFF;
            border-radius: 8px;
            border: 1px solid var(--border-color);
            padding: 10px 12px 6px;
        }

        /* BULLETS LIST */
        .bullets-list {
            list-style: none;
            margin-bottom: 25px;
        }

        .bullets-list li {
            position: relative;
            padding-left: 25px;
            margin-bottom: 10px;
            font-size: 14px;
        }

        .bullets-list li::before {
            content: "•";
            color: var(--primary);
            font-size: 22px;
            position: absolute;
            left: 5px;
            top: -4px;
        }

        /* INVESTMENT BOX */
        .investment-card {
            background: linear-gradient(135deg, var(--secondary) 0%, var(--primary) 100%);
            color: #FFFFFF;
            border-radius: 10px;
            padding: 25px 30px;
            margin-top: 20px;
            display: table;
            width: 100%;
        }

        .invest-main {
            display: table-cell;
            vertical-align: middle;
            width: 50%;
        }

        .invest-main h3 {
            font-size: 15px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            opacity: 0.95;
        }

        .invest-price {
            font-size: 32px;
            font-weight: 800;
            margin-top: 5px;
        }

        .invest-metrics {
            display: table-cell;
            vertical-align: middle;
            width: 50%;
            border-left: 1px solid rgba(255,255,255,0.3);
            padding-left: 25px;
        }

        .metric-item {
            display: inline-block;
            width: 48%;
            margin-bottom: 10px;
        }

        .metric-label {
            font-size: 11px;
            text-transform: uppercase;
            opacity: 0.85;
        }

        .metric-val {
            font-size: 16px;
            font-weight: bold;
        }

        /* FOOTER */
        .footer {
            background-color: #111111;
            color: #AAAAAA;
            padding: 20px 40px;
            font-size: 12px;
            display: table;
            width: 100%;
        }

        .footer-company {
            display: table-cell;
            vertical-align: middle;
            width: 50%;
        }

        .footer-company strong {
            color: #FFFFFF;
            font-size: 14px;
            display: block;
        }

        .footer-contact {
            display: table-cell;
            vertical-align: middle;
            text-align: right;
            width: 50%;
        }
    </style>
</head>
<body>

<div class="proposal-container">

    <div class="proposal-page">
    <div class="header">
        <div class="logo-placeholder">
            <div class="logo-box">
                ${logoSrc ? `<img src="${logoSrc}" alt="${empresaNome}" />` : ''}
            </div>
        </div>
        <div class="header-title">
            <h1>Proposta Comercial</h1>
            <p>Sistema de Energia Solar Fotovoltaica</p>
        </div>
    </div>

    <div class="content">

        <div class="grid-2">
            <div class="col-half">
                <div class="info-card">
                    <h3>Dados do Cliente</h3>
                    <ul class="info-list">
                        <li><strong>Nome:</strong> <span>${nomeCliente}</span></li>
                        ${cpfLinha}
                        <li><strong>Data da Proposta:</strong> <span>${dataProposta}</span></li>
                        <li><strong>Validade:</strong> <span>${dataValidade}</span></li>
                    </ul>
                </div>
            </div>
            <div class="col-half">
                <div class="info-card">
                    <h3>Dados do Sistema</h3>
                    <ul class="info-list">
                        <li><strong>Consumo Mensal:</strong> <span>${consumoStr}</span></li>
                        <li><strong>Potência Calculada:</strong> <span>${kwpStr}</span></li>
                        <li><strong>Inversor Recomendado:</strong> <span>${inversorStr}</span></li>
                    </ul>
                </div>
            </div>
        </div>

        <div class="about-card">
            <h3>Apresentação da Empresa</h3>
            <p>A Hangsix Energia Solar é uma empresa pioneira no setor de energia solar na região Norte do Brasil, atuando desde os primeiros anos de expansão do mercado fotovoltaico com soluções voltadas à economia, eficiência energética e qualidade técnica.</p>
            <p>Desde sua fundação, a empresa se destacou por sua atuação estratégica e pela parceria com a Elgin, contribuindo para levar energia limpa e redução de custos a clientes da região da Grande Belém e diversos municípios do interior do estado do Pará.</p>

            <div class="about-grid">
                <div class="about-col">
                    <h4>🌞 Experiência e Pioneirismo</h4>
                    <p>A Hangsix participou das fases iniciais de crescimento da energia solar na região, acumulando experiência técnica e contribuindo para a expansão do setor. Essa trajetória consolidou a empresa como referência em soluções fotovoltaicas, consultoria técnica e desenvolvimento de projetos.</p>
                    <p>Sua atuação inclui não apenas a implementação de sistemas, mas também a disseminação de conhecimento e suporte técnico para empresas integradoras do mercado solar, estabelecendo parcerias sólidas e contribuindo para o fortalecimento do setor.</p>
                </div>
                <div class="about-col">
                    <h4>🛠️ Processo 100% Próprio</h4>
                    <p>Um dos principais diferenciais da empresa é seu modelo de operação com processo totalmente próprio.</p>
                    <p>A Hangsix:</p>
                    <ul class="about-list">
                        <li>Não terceiriza sua mão de obra</li>
                        <li>Capacita e forma profissionais especializados</li>
                        <li>Controla todas as etapas do projeto</li>
                        <li>Mantém padrão rigoroso de qualidade na instalação</li>
                        <li>Desenvolve conhecimento técnico interno contínuo</li>
                    </ul>
                    <p>Esse modelo garante maior controle, confiabilidade e excelência na entrega dos projetos.</p>
                </div>
            </div>

            <div class="about-grid">
                <div class="about-col">
                    <h4>🎓 Capacitação e Desenvolvimento do Setor</h4>
                    <p>Desde 2021, a empresa atua na capacitação de parceiros e profissionais do mercado, promovendo desenvolvimento técnico, compartilhamento de conhecimento e fortalecimento da energia solar como solução sustentável e economicamente viável.</p>
                    <p>Essa atuação contribui diretamente para o crescimento do setor e para a evolução da qualidade dos serviços oferecidos na região.</p>
                </div>
                <div class="about-col">
                    <h4>⚡ Compromisso com Qualidade e Economia</h4>
                    <p>A Hangsix Energia Solar tem como missão entregar soluções que unam eficiência energética, economia real e alto padrão técnico, sempre priorizando:</p>
                    <ul class="about-list">
                        <li>Redução de custos para o cliente</li>
                        <li>Qualidade na execução</li>
                        <li>Segurança e confiabilidade dos sistemas</li>
                        <li>Parcerias duradouras</li>
                        <li>Desenvolvimento do mercado de energia solar</li>
                    </ul>
                </div>
            </div>
        </div>

    </div>
    </div>

    <div class="proposal-page proposal-page-full">
        <div class="autoconsumo-section">
            <div class="ac-banner">
                <h2>Como Funciona o Sistema de Autoconsumo Solar</h2>
                <p>Entenda o fluxo de energia fotovoltaica na sua residência de forma simples e visual</p>
            </div>
            <section class="diagram-container">
                <h3 class="diagram-title">Esquema do Fluxo Elétrico Residencial</h3>
                <div class="svg-wrapper">
                    <img src="${autoconsumoSrc}" alt="Esquema do fluxo elétrico residencial — autoconsumo solar" />
                </div>
            </section>
        </div>
    </div>

    <div class="proposal-page">
    <div class="content">
            <section class="flow-section">
                <h3 class="ac-section-heading">4 Etapas Principais do Funcionamento</h3>
                <table class="flow-grid">
                    <tr>
                        <td>
                            <div class="flow-item">
                                <span class="badge bg-orange2">Etapa 1</span>
                                <div class="flow-item-title gerada">Energia Gerada</div>
                                <p>É o total de energia limpa gerada pelos painéis solares (fotovoltaicos) a partir da radiação solar durante o período de medição. A energia é gerada em Corrente Contínua (CC).</p>
                            </div>
                        </td>
                        <td>
                            <div class="flow-item">
                                <span class="badge bg-orange1">Etapa 2</span>
                                <div class="flow-item-title auto">Auto Consumo</div>
                                <p>É a energia instantaneamente utilizada pelos eletrodomésticos da sua casa enquanto o sol está brilhando. O inversor converte CC em Corrente Alternada (CA), pronta para uso imediato.</p>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <div class="flow-item">
                                <span class="badge bg-accent">Etapa 3</span>
                                <div class="flow-item-title injetada">Energia Injetada</div>
                                <p>Quando a geração solar supera o consumo da casa durante o dia, o excesso de energia é enviado para a rede elétrica da concessionária, gerando <strong>créditos de energia</strong>.</p>
                            </div>
                        </td>
                        <td>
                            <div class="flow-item">
                                <span class="badge bg-dark">Etapa 4</span>
                                <div class="flow-item-title consumida">Energia Consumida</div>
                                <p>Durante a noite ou em momentos de consumo muito elevado, a casa utiliza energia proveniente da rede elétrica da concessionária, compensada posteriormente pelos créditos acumulados.</p>
                            </div>
                        </td>
                    </tr>
                </table>
                <div class="summary-box">
                    <strong>Resumo do Crédito Solar:</strong> Se você injetar mais energia na rede do que consumir no mês, os créditos ficam válidos por até 60 meses para abater contas futuras ou ser transferidos para outras propriedades do mesmo titular!
                </div>
            </section>

        <h2 class="section-title">Galeria de Projetos Instalados</h2>
        <div class="projects-grid">
            <div class="project-row">
                <div class="project-card">
                    <div class="project-card-inner">
                        <div class="project-img-placeholder">
                            <img src="${g0}" alt="Projeto Residencial Ananindeua" />
                        </div>
                        <div class="project-details">
                            <h4>Residencial Ananindeua</h4>
                            <p><strong>Potência:</strong> 5.5 kWp</p>
                            <p><strong>Módulos:</strong> 10x 550W</p>
                            <p><strong>Economia:</strong> ~R$ 680/mês</p>
                            <span class="project-tag">Residencial</span>
                        </div>
                    </div>
                </div>

                <div class="project-card">
                    <div class="project-card-inner">
                        <div class="project-img-placeholder">
                            <img src="${g1}" alt="Projeto Hotel Hilma" />
                        </div>
                        <div class="project-details">
                            <h4>Hotel Hilma</h4>
                            <p><strong>Potência:</strong> 38,44 kWp</p>
                            <p><strong>Módulos:</strong> 62x 620W</p>
                            <p><strong>Economia:</strong> ~R$ 4.513/mês</p>
                            <span class="project-tag">Comercial</span>
                        </div>
                    </div>
                </div>

                <div class="project-card">
                    <div class="project-card-inner">
                        <div class="project-img-placeholder">
                            <img src="${g2}" alt="Projeto Sociedade Recanto Verde" />
                        </div>
                        <div class="project-details">
                            <h4>Sociedade Recanto Verde</h4>
                            <p><strong>Potência:</strong> 52,46 kWp</p>
                            <p><strong>Módulos:</strong> 86x 610W</p>
                            <p><strong>Economia:</strong> ~R$ 6.159/mês</p>
                            <span class="project-tag">Industrial</span>
                        </div>
                    </div>
                </div>

                <div class="project-card">
                    <div class="project-card-inner">
                        <div class="project-img-placeholder">
                            <img src="${g3}" alt="Projeto São João da Ponta" />
                        </div>
                        <div class="project-details">
                            <h4>São João da Ponta</h4>
                            <p><strong>Potência:</strong> 108,81 kWp</p>
                            <p><strong>Módulos:</strong> 186x 585W</p>
                            <p><strong>Economia:</strong> ~R$ 12.777/mês</p>
                            <span class="project-tag">Rural</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </div>
    </div>

    <div class="proposal-page">
    <div class="content">
        <h2 class="section-title">Projeção de Economia Acumulada</h2>
        <div class="chart-container">
            <div class="chart-header">
                <div class="chart-title-box">
                    <h3>Retorno do investimento (R$)</h3>
                </div>
                <div class="chart-legend">
                    <div class="legend-item">
                        <span class="legend-color" style="background-color: #FA670A;"></span>Economia acumulada
                    </div>
                    <div class="legend-item">
                        <span class="legend-line"></span>Valor do investimento
                    </div>
                </div>
            </div>
            <div class="chart-svg-box">
                ${chartSvg}
            </div>
        </div>

        <h2 class="section-title">Resumo do Investimento</h2>
        <div class="investment-card">
            <div class="invest-main">
                <h3>Valor Total do Sistema</h3>
                <div class="invest-price">${investimentoStr}</div>
                <p style="font-size: 11px; opacity: 0.9; margin-top: 5px;">*Incluso equipamentos, engenharia, instalação e homologação.</p>
            </div>
            <div class="invest-metrics">
                <div class="metric-item">
                    <div class="metric-label">Redução na Conta</div>
                    <div class="metric-val">Até 85%</div>
                </div>
                <div class="metric-item">
                    <div class="metric-label">Economia Anual</div>
                    <div class="metric-val">${economiaAnualStr}</div>
                </div>
                <div class="metric-item">
                    <div class="metric-label">Payback Estimado</div>
                    <div class="metric-val">${paybackAnosStr}</div>
                </div>
                <div class="metric-item">
                    <div class="metric-label">Valorização</div>
                    <div class="metric-val">+ 6%</div>
                </div>
            </div>
        </div>

        <div class="grid-2">
            <div class="col-half">
                <h2 class="section-title">Especificações Técnicas</h2>
                <div class="info-card">
                    <ul class="info-list">
                        <li><strong>Módulos:</strong> <span>${modulosStr}</span></li>
                        <li><strong>Inversor:</strong> <span>${inversorStr} On-Grid</span></li>
                        <li><strong>Estrutura:</strong> <span>Fibrocimento</span></li>
                        <li><strong>Proteção:</strong> <span>Interna do inversor</span></li>
                    </ul>
                </div>
            </div>
            <div class="col-half">
                <h2 class="section-title">Garantias e Serviços</h2>
                <ul class="bullets-list">
                    <li>Garantia de 15 anos de eficiência dos módulos</li>
                    <li>Garantia de 10 anos para o Inversor Solar</li>
                    <li>Garantia de 1 ano no serviço de instalação</li>
                    <li>Homologação completa junto à concessionária</li>
                </ul>
            </div>
        </div>

    </div>

    <div class="footer">
        <div class="footer-company">
            <strong>${empresaNome}</strong>
            ${empresaSlogan ? `<span>${empresaSlogan}</span>` : ''}
        </div>
        <div class="footer-contact">
            ${contatoStr ? `<p>${contatoStr}</p>` : ''}
            ${enderecoStr ? `<p>${enderecoStr}</p>` : ''}
        </div>
    </div>
    </div>

</div>

</body>
</html>`
}
